import { useGetAanmeldingenOnDateForUserQuery } from "../lib/model/kantoordagApi.ts";
import { ROOM__NO_ROOM } from "../app/Constants.tsx";
import { FaBuilding } from "react-icons/fa6";
import { GiOfficeChair } from "react-icons/gi";
import { FaCar, FaParking } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

export interface TodaysReservationProps {
    userId: string,
    compact?: boolean,
}

export default function TodaysReservation({ userId, compact = false }: TodaysReservationProps) {
    const { t } = useTranslation("reservations");
    const todayIso = DateTime.now().toISODate();
    // Query for the "Today" summary block
    const todayQuery = useGetAanmeldingenOnDateForUserQuery(
        { userId: userId || '', datum: todayIso },
        { skip: !userId || !todayIso, refetchOnMountOrArgChange: true }
    );

    const todayReservations = todayQuery.data ? [todayQuery.data] : [];

    if (todayQuery.isLoading) {
        return null; // Or a loading spinner
    }

    if (todayReservations.length === 0) {
        return (
            <div className="flex flex-col items-center space-y-3 mb-2">
                <div className="flex flex-col gap-1 items-center justify-center p-2 border-l-4 border-blue-500 bg-gray-50 dark:bg-gray-800 w-full max-w-xs">
                    <p className="text-gray-500 italic text-center">{t('no_reservation_today')}</p>
                </div>
            </div>
        );
    }

    const res = todayReservations[0]; // Assuming only one reservation per day for simplicity in this component

    if (compact) {
        return (
            <div className="flex items-center justify-center mb-2">
                <div className="flex items-center justify-center p-2 border-l-4 border-blue-500 bg-gray-50 dark:bg-gray-800 w-full max-w-sm">
                    <strong className="mr-2">{t('today')}:</strong>
                    {res.room === ROOM__NO_ROOM ? (
                        <span className="flex items-center mr-4">
                            <FaBuilding className="mr-1 text-gray-600"/>
                            <span>{res.building}</span>
                        </span>
                    ) : (
                        <span className="flex items-center mr-1">
                            <GiOfficeChair className="mr-1 text-gray-600"/>
                            <span>{res.room}</span>
                        </span>
                    )}
                    {res.parkingRequested && (
                        <span className="flex items-center">
                            <span className="mx-2 text-gray-400">|</span>
                            {res.parkingAssigned ? (
                                <FaParking className="text-blue-500" title={t("parking_assigned")}/>
                            ) : (
                                <FaCar className="text-gray-500" title={t("parking_reservation")}/>
                            )}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center space-y-3 mb-2">
            <div className="flex flex-col gap-1 items-center justify-center p-4 border-l-4 border-blue-500 bg-gray-50 dark:bg-gray-800 w-full max-w-2xs">
                <div className="flex items-center">
                    <strong>{t('today')}</strong>
                </div>
                {res.room === ROOM__NO_ROOM ? (
                    <div className="flex items-center">
                        <FaBuilding className="mr-2 text-gray-600"/>
                        <span>{res.building}</span>
                    </div>
                ) : (
                    <div className="flex items-center">
                        <GiOfficeChair className="mr-2 text-gray-600"/>
                        <span>{res.room}</span>
                    </div>
                )}
                {res.parkingRequested && (
                    <div className="flex items-center">
                        {res.parkingAssigned ? (
                            <><FaParking className="mr-2 text-blue-500"/> <span>{t("parking_assigned")}</span></>
                        ) : (
                            <><FaCar className="mr-2 text-gray-500"/> <span>{t("parking_reservation")}</span></>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}